/**
 * useAllocationEvents — TanStack Query mutations for allocation events
 *
 * Movement ledger model: each departure/arrival creates directional schedule records.
 * The DB trigger handles updating allocations.presence automatically.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import { createAllocationEvent } from '@/lib/api/allocation-events'
import { createAircraftSchedule, updateAircraftSchedule } from '@/lib/api/aircraft-schedules'
import { updateLease } from '@/lib/api/leases'
import { supabase } from '@/lib/supabase'
import { broadcastInvalidation } from '@/lib/sync'

// ============================================
// Invalidation helper
// ============================================

function invalidateAll(queryClient: ReturnType<typeof useQueryClient>) {
  const keys = [
    ['aircraft-movement-log'],
    ['map-allocations'],
    ['aircraft-schedules'],
    ['flight-line-schedules'],
    ['aircraft-flights'],
  ] as const
  keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
  broadcastInvalidation(keys)
}

// ============================================
// Departure Event
// ============================================

interface DepartureEventInput {
  allocation_id: string
  aircraft_id: string
  location_id: string
  event_at?: string | null
  expected_return_at?: string | null
  destination_icao?: string | null
  destination_name?: string | null
  confidence?: 'confirmed' | 'tentative'
  notes?: string | null
}

/**
 * Creates a 'departed' event and outbound schedule record.
 * If expected_return_at is provided, also creates a paired inbound record.
 * The DB trigger sets allocations.presence = 'away'.
 */
export function useCreateDepartureEvent() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (input: DepartureEventInput) => {
      const organizationId = currentOrganization?.id
      if (!organizationId) throw new Error('No organization selected')

      const eventAt = input.event_at ?? new Date().toISOString()

      // 1. Create the departure event
      const event = await createAllocationEvent(organizationId, {
        allocation_id: input.allocation_id,
        aircraft_id: input.aircraft_id,
        location_id: input.location_id,
        event_type: 'departed',
        event_source: 'manual',
        event_at: input.event_at ?? null,
        expected_return_at: input.expected_return_at ?? null,
        notes: input.notes ?? null,
      })

      // 2. Create outbound schedule (completed — already departed)
      const outbound = await createAircraftSchedule(organizationId, {
        aircraft_id: input.aircraft_id,
        location_id: input.location_id,
        direction: 'outbound',
        scheduled_at: eventAt,
        source: 'csr_entered',
        confidence: input.confidence ?? 'confirmed',
        status: 'completed',
        actual_at: eventAt,
        event_id: event.id,
        airport_icao: input.destination_icao ?? undefined,
        airport_name: input.destination_name ?? undefined,
      })

      // 3. If expected return, create paired inbound record
      let inbound = null
      if (input.expected_return_at) {
        inbound = await createAircraftSchedule(organizationId, {
          aircraft_id: input.aircraft_id,
          location_id: input.location_id,
          direction: 'inbound',
          scheduled_at: input.expected_return_at,
          source: 'csr_entered',
          confidence: input.confidence ?? 'confirmed',
          status: 'scheduled',
          pair_id: outbound.id,
          airport_icao: input.destination_icao ?? undefined,
          airport_name: input.destination_name ?? undefined,
        })
      }

      return { event, outbound, inbound }
    },
    onSuccess: () => invalidateAll(queryClient),
  })
}

// ============================================
// Arrival Event
// ============================================

interface ArrivalEventInput {
  allocation_id: string
  aircraft_id: string
  location_id: string
  notes?: string | null
}

/**
 * Creates an 'arrived' event and resolves any pending inbound schedule.
 * The DB trigger sets allocations.presence = 'present'.
 */
export function useCreateArrivalEvent() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (input: ArrivalEventInput) => {
      const organizationId = currentOrganization?.id
      if (!organizationId) throw new Error('No organization selected')

      // 1. Create the arrival event
      const event = await createAllocationEvent(organizationId, {
        allocation_id: input.allocation_id,
        aircraft_id: input.aircraft_id,
        location_id: input.location_id,
        event_type: 'arrived',
        event_source: 'manual',
        notes: input.notes ?? null,
      })

      // 2. Find and resolve any pending inbound schedules for this aircraft+location
      const { data: pendingInbound } = await supabase
        .from('aircraft_schedules')
        .select('id')
        .eq('aircraft_id', input.aircraft_id)
        .eq('location_id', input.location_id)
        .eq('direction', 'inbound')
        .in('status', ['scheduled', 'confirmed'])

      if (pendingInbound && pendingInbound.length > 0) {
        const now = new Date().toISOString()
        for (const sched of pendingInbound) {
          await updateAircraftSchedule(sched.id, {
            status: 'completed',
            event_id: event.id,
            actual_at: now,
          })
        }
      }

      return { event }
    },
    onSuccess: () => invalidateAll(queryClient),
  })
}

// ============================================
// Final Departure Event (Checkout / Permanent Leave)
// ============================================

interface FinalDepartureEventInput {
  allocation_id: string
  aircraft_id: string
  location_id: string
  notes?: string | null
}

/**
 * Creates a 'departed_final' event.
 * The DB trigger sets allocations.presence = 'departed' and status = 'completed'.
 */
export function useCreateFinalDepartureEvent() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (input: FinalDepartureEventInput) => {
      const organizationId = currentOrganization?.id
      if (!organizationId) throw new Error('No organization selected')

      const event = await createAllocationEvent(organizationId, {
        allocation_id: input.allocation_id,
        aircraft_id: input.aircraft_id,
        location_id: input.location_id,
        event_type: 'departed_final',
        event_source: 'manual',
        notes: input.notes ?? null,
      })

      return { event }
    },
    onSuccess: () => invalidateAll(queryClient),
  })
}

// ============================================
// Check-In Event (Reserved/Expected → Active/Present)
// ============================================

interface CheckInEventInput {
  allocation_id: string
  aircraft_id: string
  location_id: string
  /** If transient stay, update its status to 'checked_in' */
  transient_stay_id?: string | null
  notes?: string | null
}

/**
 * Creates a 'placed' event for first-time check-in.
 * The DB trigger sets allocations.status = 'active', presence = 'present'.
 * If a transient_stay_id is provided, also updates the transient stay status.
 */
export function useCheckInEvent() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (input: CheckInEventInput) => {
      const organizationId = currentOrganization?.id
      if (!organizationId) throw new Error('No organization selected')

      // 1. Create the 'placed' event — trigger sets active/present
      const event = await createAllocationEvent(organizationId, {
        allocation_id: input.allocation_id,
        aircraft_id: input.aircraft_id,
        location_id: input.location_id,
        event_type: 'placed',
        event_source: 'manual',
        notes: input.notes ?? null,
      })

      // 2. If transient stay, update its status to 'checked_in'
      if (input.transient_stay_id) {
        await supabase
          .from('transient_stays')
          .update({ status: 'checked_in' })
          .eq('id', input.transient_stay_id)
      }

      // 3. Resolve any pending inbound schedules (direction='inbound')
      const { data: pendingSchedules } = await supabase
        .from('aircraft_schedules')
        .select('id')
        .eq('aircraft_id', input.aircraft_id)
        .eq('location_id', input.location_id)
        .eq('direction', 'inbound')
        .in('status', ['scheduled', 'confirmed'])

      if (pendingSchedules && pendingSchedules.length > 0) {
        const now = new Date().toISOString()
        for (const sched of pendingSchedules) {
          await updateAircraftSchedule(sched.id, {
            status: 'completed',
            event_id: event.id,
            actual_at: now,
          })
        }
      }

      return { event }
    },
    onSuccess: () => invalidateAll(queryClient),
  })
}

// ============================================
// Check-Out Event (Active → Completed/Departed)
// ============================================

interface CheckOutEventInput {
  allocation_id: string
  aircraft_id: string
  location_id: string
  /** If transient stay, update its status to 'checked_out' */
  transient_stay_id?: string | null
  notes?: string | null
}

/**
 * Creates a 'departed_final' event for permanent check-out.
 * The DB trigger sets allocations.status = 'completed', presence = 'departed'.
 * If a transient_stay_id is provided, also updates the transient stay status.
 */
export function useCheckOutEvent() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (input: CheckOutEventInput) => {
      const organizationId = currentOrganization?.id
      if (!organizationId) throw new Error('No organization selected')

      // 1. Create the 'departed_final' event — trigger sets completed/departed
      const event = await createAllocationEvent(organizationId, {
        allocation_id: input.allocation_id,
        aircraft_id: input.aircraft_id,
        location_id: input.location_id,
        event_type: 'departed_final',
        event_source: 'manual',
        notes: input.notes ?? null,
      })

      // 2. If transient stay, update its status to 'checked_out'
      if (input.transient_stay_id) {
        await supabase
          .from('transient_stays')
          .update({
            status: 'checked_out',
            actual_check_out: new Date().toISOString(),
          })
          .eq('id', input.transient_stay_id)
      }

      // 3. Resolve any non-terminal schedules
      const { data: activeSchedules } = await supabase
        .from('aircraft_schedules')
        .select('id')
        .eq('aircraft_id', input.aircraft_id)
        .eq('location_id', input.location_id)
        .in('status', ['scheduled', 'confirmed'])

      if (activeSchedules && activeSchedules.length > 0) {
        for (const sched of activeSchedules) {
          await updateAircraftSchedule(sched.id, {
            status: 'completed',
          })
        }
      }

      return { event }
    },
    onSuccess: () => invalidateAll(queryClient),
  })
}

// ============================================
// Final Departure + Lease Complete (composed)
// ============================================

interface FinalDepartureWithLeaseInput {
  allocation_id: string
  aircraft_id: string
  location_id: string
  lease_id: string
  notes?: string | null
}

/**
 * Composed action: creates a 'departed_final' event (allocation -> completed/departed)
 * AND marks the lease contract as 'completed'.
 * Used from ContractStatusRow quick actions for expiring/expired leases.
 */
export function useFinalDepartureWithLeaseComplete() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (input: FinalDepartureWithLeaseInput) => {
      const organizationId = currentOrganization?.id
      if (!organizationId) throw new Error('No organization selected')

      // 1. Create departed_final event — DB trigger sets allocation to completed/departed
      const event = await createAllocationEvent(organizationId, {
        allocation_id: input.allocation_id,
        aircraft_id: input.aircraft_id,
        location_id: input.location_id,
        event_type: 'departed_final',
        event_source: 'manual',
        notes: input.notes ?? null,
      })

      // 2. Mark lease as completed
      await updateLease(input.lease_id, { status: 'completed' })

      // 3. Resolve any non-terminal schedules
      const { data: activeSchedules } = await supabase
        .from('aircraft_schedules')
        .select('id')
        .eq('aircraft_id', input.aircraft_id)
        .eq('location_id', input.location_id)
        .in('status', ['scheduled', 'confirmed'])

      if (activeSchedules && activeSchedules.length > 0) {
        for (const sched of activeSchedules) {
          await updateAircraftSchedule(sched.id, { status: 'completed' })
        }
      }

      return { event }
    },
    onSuccess: () => {
      invalidateAll(queryClient)
      queryClient.invalidateQueries({ queryKey: ['leases'] })
      broadcastInvalidation([['leases']])
    },
  })
}

// ============================================
// Confirm Scheduled Departure (link to existing schedule)
// ============================================

interface ConfirmScheduledDepartureInput {
  schedule_id: string
  allocation_id: string
  aircraft_id: string
  location_id: string
  destination_icao?: string | null
  notes?: string | null
}

/**
 * Confirms a pre-scheduled departure by creating a 'departed' event
 * and linking it to the existing outbound schedule. The paired inbound
 * (if any) already exists from when the schedule was created.
 */
export function useConfirmScheduledDeparture() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async (input: ConfirmScheduledDepartureInput) => {
      const organizationId = currentOrganization?.id
      if (!organizationId) throw new Error('No organization selected')

      const now = new Date().toISOString()

      // 1. Create the departure event
      const event = await createAllocationEvent(organizationId, {
        allocation_id: input.allocation_id,
        aircraft_id: input.aircraft_id,
        location_id: input.location_id,
        event_type: 'departed',
        event_source: 'manual',
        notes: input.notes ?? null,
      })

      // 2. Update the outbound schedule: mark as completed, link the event
      await updateAircraftSchedule(input.schedule_id, {
        status: 'completed',
        event_id: event.id,
        actual_at: now,
      })

      return { event }
    },
    onSuccess: () => invalidateAll(queryClient),
  })
}
